import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { User, Session } from '@supabase/supabase-js';

// Helper function to get the correct base URL for redirects
const getBaseUrl = (): string => {
  // In production, use the production URL
  if (process.env.NODE_ENV === 'production') {
    return 'https://lotushealingspa.com';
  }
  // In development, use localhost
  return window.location.origin;
};

// Define UserProfile type from user_profiles table
interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  preferred_name: string | null;
  phone_number: string | null;
  role: string;
  account_status: string | null;
  membership_status: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  wallet_balance: number;  // Wallet balance for prepaid credits
  profile_picture_url: string | null;
  birth_date: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  country: string | null;
  gender: string | null;
  pronouns: string | null;
  language_preference: string | null;
  created_at: string;
  updated_at: string;
}

// Member service credit type
export interface MemberCredit {
  serviceId: string;
  serviceName: string;
  quantity: number;
  maxQuantity: number;
}

// Define the shape of our AuthContext
interface AuthContextType {
  user: User | null;
  session: Session | null;
  userProfile: UserProfile | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isCrew: boolean;
  isCustomer: boolean;
  isMember: boolean;  // Has active membership (for 20% discount)
  isLoading: boolean;
  memberCredits: MemberCredit[];  // Free service credits for members
  login: (email: string, password: string) => Promise<{ error: any; success: boolean }>;
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<{ error: any; success: boolean; user: User | null }>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any; success: boolean }>;
  updateProfile: (data: Record<string, any>) => Promise<{ error: any; success: boolean }>;
  refreshUserProfile: () => Promise<void>;
  refreshMemberCredits: () => Promise<void>;
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  userProfile: null,
  isAuthenticated: false,
  isAdmin: false,
  isCrew: false,
  isCustomer: false,
  isMember: false,
  isLoading: true,
  memberCredits: [],
  login: async () => ({ error: null, success: false }),
  signUp: async () => ({ error: null, success: false, user: null }),
  logout: async () => {},
  resetPassword: async () => ({ error: null, success: false }),
  updateProfile: async () => ({ error: null, success: false }),
  refreshUserProfile: async () => {},
  refreshMemberCredits: async () => {},
});

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

// Provider component that wraps app and makes auth available to any child component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [memberCredits, setMemberCredits] = useState<MemberCredit[]>([]);

  // Fetch member credits for active members
  const fetchMemberCredits = async (userId: string): Promise<MemberCredit[]> => {
    try {
      const { data, error } = await supabase.rpc('get_member_credits', {
        p_user_id: userId
      });

      if (error) {
        console.error('Error fetching member credits:', error);
        return [];
      }

      const credits = (data || []).map((row: any) => ({
        serviceId: row.service_id,
        serviceName: row.service_name,
        quantity: row.quantity,
        maxQuantity: row.max_quantity
      }));

      setMemberCredits(credits);
      return credits;
    } catch (error) {
      console.error('Error fetching member credits:', error);
      return [];
    }
  };

  // Public function to refresh member credits
  const refreshMemberCredits = async () => {
    if (user?.id && userProfile?.membership_status === 'active') {
      await fetchMemberCredits(user.id);
    } else {
      setMemberCredits([]);
    }
  };

  // Fetch user profile from user_profiles table
  const fetchUserProfile = async (userId: string, retryCount = 0): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        // Handle both PGRST116 (no rows) and 406 (RLS violation / no acceptable response)
        // Both can occur when profile doesn't exist for the auth user
        const isProfileMissing = error.code === 'PGRST116' ||
          error.message?.includes('406') ||
          (error as any).status === 406;

        // Retry a few times to allow trigger to complete
        if (isProfileMissing && retryCount < 3) {
          // Wait 1 second and retry
          await new Promise(resolve => setTimeout(resolve, 1000));
          return fetchUserProfile(userId, retryCount + 1);
        }

        // After retries, use the ensure_user_profile RPC as fallback
        // This handles orphaned auth users and guest-to-customer migration
        if (isProfileMissing && retryCount >= 3) {
          console.log('Profile not found after retries, calling ensure_user_profile RPC');
          try {
            const { data: rpcResult, error: rpcError } = await supabase
              .rpc('ensure_user_profile');

            if (rpcError) {
              console.error('ensure_user_profile RPC failed:', rpcError);
              // Fall back to direct upsert as last resort
              const { data: authUser } = await supabase.auth.getUser();
              if (authUser.user) {
                const { data: newProfile, error: upsertError } = await supabase
                  .from('user_profiles')
                  .upsert({
                    id: userId,
                    email: authUser.user.email || '',
                    full_name: authUser.user.user_metadata?.full_name || '',
                    phone_number: authUser.user.user_metadata?.phone_number || '',
                    role: 'customer',
                    account_status: 'active'
                  }, {
                    onConflict: 'id'
                  })
                  .select()
                  .single();

                if (!upsertError && newProfile) {
                  setUserProfile(newProfile as UserProfile);
                  return newProfile as UserProfile;
                }
              }
              return null;
            }

            // RPC succeeded - now fetch the profile
            if (rpcResult?.success) {
              console.log('ensure_user_profile succeeded:', rpcResult.action);
              const { data: profile, error: fetchError } = await supabase
                .from('user_profiles')
                .select('*')
                .eq('id', userId)
                .single();

              if (!fetchError && profile) {
                setUserProfile(profile as UserProfile);
                return profile as UserProfile;
              }
            }
          } catch (rpcErr) {
            console.error('Error calling ensure_user_profile:', rpcErr);
          }
        }

        return null;
      }

      // Defensive check: If account is deleted, force logout on this device
      if (data?.account_status === 'deleted') {
        console.log('Account deleted - forcing logout');
        await supabase.auth.signOut({ scope: 'local' });
        setUserProfile(null);
        setUser(null);
        setSession(null);
        // Redirect to home
        window.location.href = '/';
        return null;
      }

      setUserProfile(data as UserProfile);

      // Fetch member credits if user is an active member
      if (data?.membership_status === 'active') {
        await fetchMemberCredits(userId);
      } else {
        setMemberCredits([]);
      }

      return data as UserProfile;
    } catch (error) {
      return null;
    }
  };

  // Public function to refresh user profile
  const refreshUserProfile = async () => {
    if (user?.id) {
      await fetchUserProfile(user.id);
    }
  };

  // Check if user is already logged in on mount
  useEffect(() => {
    // Get the current session
    const fetchSession = async () => {
      try {
        const { data } = await supabase.auth.getSession();

        if (data.session) {
          setSession(data.session);
          setUser(data.session.user);
          // Fetch user profile - MUST await to ensure role is loaded
          await fetchUserProfile(data.session.user.id);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, currentSession) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);

      if (currentSession?.user) {
        // Fire and forget - profile will update when ready
        fetchUserProfile(currentSession.user.id);
      } else {
        setUserProfile(null);
      }
      // Don't set isLoading here - only on initial mount
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Login function
  const login = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { error, success: false };
      }

      return { error: null, success: true };
    } catch (error) {
      return { error, success: false };
    }
  };

  // Sign up function with optional metadata
  const signUp = async (email: string, password: string, metadata?: Record<string, any>) => {
    try {
      // Create a race between the signup and a timeout to handle hanging requests
      const signupPromise = supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata || {},
          emailRedirectTo: getBaseUrl() + '/login'
        }
      });

      const result = await Promise.race([
        signupPromise,
        new Promise<any>((resolve) => {
          setTimeout(async () => {
            // Check if user was created despite timeout
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
              resolve({ data: { user }, error: null });
            } else {
              resolve({ data: null, error: { message: 'Signup timeout' } });
            }
          }, 3000);
        })
      ]);

      const { data, error } = result;

      if (error) {
        return { error, success: false, user: null };
      }

      return { error: null, success: true, user: data?.user || null };
    } catch (error: any) {
      return { error, success: false, user: null };
    }
  };

  // Logout function (local scope - only logs out current device)
  // Handles expired sessions gracefully - clears local state even if API returns 403
  const logout = async () => {
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      // If signOut fails (e.g., 403 due to expired token), that's fine
      // The session was already invalid - we just need to clear local state
      console.log('SignOut API call failed (session likely expired), clearing local state');
    }

    // Always clear local state regardless of API response
    // This ensures user can log out even with expired/invalid tokens
    setUser(null);
    setSession(null);
    setUserProfile(null);
    setMemberCredits([]);

    // Clear any cached auth data from localStorage
    // Supabase stores tokens with keys like 'sb-{projectRef}-auth-token'
    const keysToRemove = Object.keys(localStorage).filter(key =>
      key.startsWith('sb-') && key.includes('auth')
    );
    keysToRemove.forEach(key => localStorage.removeItem(key));
  };

  // Reset password function
  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: getBaseUrl() + '/reset-password',
      });

      if (error) {
        return { error, success: false };
      }

      return { error: null, success: true };
    } catch (error) {
      return { error, success: false };
    }
  };

  // Update user profile
  const updateProfile = async (data: Record<string, any>) => {
    try {
      const { error } = await supabase.auth.updateUser({
        data
      });

      if (error) {
        return { error, success: false };
      }

      return { error: null, success: true };
    } catch (error) {
      return { error, success: false };
    }
  };

  // Value object that will be passed to consumers
  const value = {
    user,
    session,
    userProfile,
    isAuthenticated: !!user,
    isAdmin: userProfile?.role === 'admin',
    isCrew: userProfile?.role === 'crew',
    isCustomer: userProfile?.role === 'customer',
    isMember: userProfile?.membership_status === 'active',
    isLoading,
    memberCredits,
    login,
    signUp,
    logout,
    resetPassword,
    updateProfile,
    refreshUserProfile,
    refreshMemberCredits,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
